import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, AlertCircle, CheckCircle2, XCircle, Loader2, Download, Search, RefreshCw, Brain } from "lucide-react";
import Papa from "papaparse";
import { useToast } from "@/hooks/use-toast";

interface UnevaluatedResponse {
  question_response_id: string;
  test_response_id: string;
  question_id: string;
  question_type: string;
  candidate_email: string;
  candidate_name: string;
  test_title: string;
  test_id: string;
  created_at: string;
  transcription_status: string;
  evaluation_status: string;
  evaluation_attempts: number;
  error_message: string;
  has_transcription: boolean;
  has_ai_feedback: boolean;
}

interface RetriggerResult {
  success: boolean;
  updated: number;
  total: number;
  errors?: string[];
  message?: string;
}

export default function EvaluationRecovery() {
  const { toast } = useToast();
  
  // Step 1: Find unevaluated
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(new Date(Date.now() + 86400000).toISOString().split('T')[0]);
  const [searching, setSearching] = useState(false);
  const [unevaluatedResults, setUnevaluatedResults] = useState<UnevaluatedResponse[] | null>(null);
  
  // Step 2: Retrigger
  const [uploadedIds, setUploadedIds] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [retriggering, setRetriggering] = useState(false);
  const [retriggerResult, setRetriggerResult] = useState<RetriggerResult | null>(null);

  // Step 1: Find unevaluated responses
  const handleFindUnevaluated = async () => {
    setSearching(true);
    setUnevaluatedResults(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('find-unevaluated-responses', {
        body: { 
          startDate: `${startDate}T00:00:00.000Z`,
          endDate: `${endDate}T23:59:59.999Z`
        }
      });

      if (error) throw error;

      if (data.success) {
        setUnevaluatedResults(data.responses);
        toast({
          title: "Search Complete",
          description: `Found ${data.count} unevaluated responses`,
        });
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error finding unevaluated:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  // Download CSV
  const downloadUnevaluatedCSV = () => {
    if (!unevaluatedResults) return;
    
    const csv = Papa.unparse(unevaluatedResults);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `unevaluated-responses-${startDate}-to-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Step 2: Handle CSV upload
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const parseCSV = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const ids = results.data
          .map((row: any) => row.question_response_id)
          .filter((id: string) => id && id.trim());
        
        setUploadedIds(ids);
        setRetriggerResult(null);
        
        toast({
          title: "CSV Loaded",
          description: `Found ${ids.length} question response IDs`,
        });
      },
      error: (error) => {
        toast({
          title: "Parse Error",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file && file.type === "text/csv") {
      parseCSV(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseCSV(file);
    }
  };

  // Retrigger evaluation
  const handleRetriggerEvaluation = async () => {
    if (uploadedIds.length === 0) return;
    
    setRetriggering(true);
    setRetriggerResult(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('trigger-batch-re-evaluation', {
        body: { question_response_ids: uploadedIds }
      });

      if (error) throw error;

      setRetriggerResult({
        success: data.success,
        updated: data.updated,
        total: data.total,
        errors: data.errors,
        message: data.message
      });

      toast({
        title: data.success ? "Retrigger Complete" : "Retrigger Completed with Errors",
        description: `Updated ${data.updated}/${data.total} responses`,
        variant: data.success ? "default" : "destructive",
      });
    } catch (error: any) {
      console.error('Error retriggering:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setRetriggering(false);
    }
  };

  // Use results from Step 1 directly
  const useStep1Results = () => {
    if (!unevaluatedResults) return;
    const ids = unevaluatedResults.map(r => r.question_response_id);
    setUploadedIds(ids);
    setRetriggerResult(null);
    toast({
      title: "IDs Loaded",
      description: `Loaded ${ids.length} IDs from search results`,
    });
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Find Unevaluated */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Step 1: Find Unevaluated Responses
          </CardTitle>
          <CardDescription>
            Search for question responses that are transcribed but not yet evaluated
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="eval-start-date">Start Date</Label>
              <Input
                id="eval-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eval-end-date">End Date</Label>
              <Input
                id="eval-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleFindUnevaluated} disabled={searching}>
            {searching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Find Unevaluated
              </>
            )}
          </Button>

          {/* Results */}
          {unevaluatedResults && (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Found <strong>{unevaluatedResults.length}</strong> unevaluated responses (transcribed but not evaluated)
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button variant="outline" onClick={downloadUnevaluatedCSV}>
                  <Download className="mr-2 h-4 w-4" />
                  Download CSV
                </Button>
                <Button variant="secondary" onClick={useStep1Results}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Use for Step 2
                </Button>
              </div>

              {unevaluatedResults.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[300px] overflow-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead>Response ID</TableHead>
                          <TableHead>Candidate</TableHead>
                          <TableHead>Test</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Eval Status</TableHead>
                          <TableHead>Attempts</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unevaluatedResults.slice(0, 100).map((r) => (
                          <TableRow key={r.question_response_id}>
                            <TableCell className="font-mono text-xs">
                              {r.question_response_id.slice(0, 8)}...
                            </TableCell>
                            <TableCell className="text-sm">
                              {r.candidate_email}
                            </TableCell>
                            <TableCell className="text-sm max-w-[200px] truncate">
                              {r.test_title}
                            </TableCell>
                            <TableCell className="text-xs">
                              {new Date(r.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-xs">
                              {r.evaluation_status}
                            </TableCell>
                            <TableCell className="text-xs">
                              {r.evaluation_attempts}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {unevaluatedResults.length > 100 && (
                    <div className="p-2 text-center text-sm text-muted-foreground border-t">
                      Showing first 100 of {unevaluatedResults.length} results. Download CSV for full list.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Retrigger Evaluation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Step 2: Retrigger Evaluation
          </CardTitle>
          <CardDescription>
            Upload a CSV with question_response_id column to reset and retrigger evaluation (preserves transcription)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Upload Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-border'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="mb-4 text-sm text-muted-foreground">
              Drag and drop CSV file, or click to browse
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileInput}
              className="hidden"
              id="eval-retrigger-file-upload"
            />
            <label htmlFor="eval-retrigger-file-upload">
              <Button variant="outline" asChild>
                <span>Choose File</span>
              </Button>
            </label>
          </div>

          {/* Preview */}
          {uploadedIds.length > 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>{uploadedIds.length} IDs loaded.</strong> Ready to retrigger evaluation.
                <div className="mt-2 text-xs text-muted-foreground">
                  First 3: {uploadedIds.slice(0, 3).map(id => id.slice(0, 8) + '...').join(', ')}
                  {uploadedIds.length > 3 ? '...' : ''}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Retrigger Button */}
          {uploadedIds.length > 0 && !retriggering && (
            <Button onClick={handleRetriggerEvaluation} className="w-full">
              <Brain className="mr-2 h-4 w-4" />
              Retrigger Evaluation for {uploadedIds.length} Responses
            </Button>
          )}

          {/* Progress */}
          {retriggering && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Resetting evaluation status... Responses will be processed by the next batch run.
              </AlertDescription>
            </Alert>
          )}

          {/* Results */}
          {retriggerResult && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Reset
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {retriggerResult.updated}
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                      Total
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {retriggerResult.total}
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-600" />
                      Errors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">
                      {retriggerResult.errors?.length || 0}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Message */}
              {retriggerResult.message && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    {retriggerResult.message}
                  </AlertDescription>
                </Alert>
              )}

              {/* Errors */}
              {retriggerResult.errors && retriggerResult.errors.length > 0 && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium mb-2">Errors:</div>
                    <ul className="text-xs space-y-1 max-h-[100px] overflow-auto">
                      {retriggerResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
